import { Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { MouseEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { RootState } from '../../app/store';
import { selectSelectedItemCartId } from '../../selectors/dialog';
import { CartItem } from '../../utils/cart';
import Colors from '../../utils/color';
import { convertModGroupToMap, sortModGroups } from '../../utils/menu';
import CartItemChildModGroup from './CartItemChildModGroup';
import TruncationTooltip from '../ItemBuilder/components/TruncationTooltip';
import { useCartScope, useIsCartItemSelected } from './CartScopeContext';
import { MAIN_CART_SCOPE } from '../../redux/features/draftCart/cartScope';
import { dialogActions } from '../../reducers/dialogSlice';
import { selectIsAgentActionBlocked } from '../../redux/features/agentMode/agentMode.selector';
import { convertToMap } from '../../utils';
import { ParsedModifierGroup } from '../../types/menu';

interface ICartItemModGroups {
  cartItem: CartItem;
  modGroupCartItem: CartItem;
  quantity: number;
  selectable?: boolean;
  // Menu-module error paths relative to this level: each path[0] is a modifier
  // group name at this depth. Empty when there are no errors to surface.
  errorNodePaths?: string[][];
}

const useStyles = makeStyles((theme: Theme) => ({
  cartItemModGroupClass: {
    paddingLeft: theme.spacing(1.75),
  },
  // HITL-1687: one line, cut with an ellipsis. The only break opportunity in
  // `! make_it_a_combo_burger_test` is the space after the `!`, so the bang sat
  // alone and the unbreakable name ran off the card — worst in Shadow's
  // half-width Live Order column. The full name moves to a hover tooltip.
  modGroupErrorClass: {
    fontSize: `${theme.spacing(1.5)} !important`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  selectedModGroupClass: {
    backgroundColor: Colors.alto,
  },
}));

export default function CartItemModGroups({
  modGroupCartItem,
  cartItem,
  quantity,
  selectable,
  errorNodePaths = [],
}: ICartItemModGroups) {
  const dispatch = useAppDispatch();
  const isAgentActionBlocked = useSelector(selectIsAgentActionBlocked);
  const { selectedModGroupPath } = useSelector(
    (state: RootState) => state.dialog
  );
  const selectedItemCartId = useSelector(selectSelectedItemCartId);
  // HITL-1617: which cart these rows belong to. `main` outside the draft
  // provider, so the AI column is unchanged.
  const renderedCartScope = useCartScope();
  // HITL-1614: `selectedItemCartId` is one number over one id space, but this
  // component now renders in BOTH carts and each numbers its ids from 1. Scoped
  // so a main-cart selection cannot light up the draft row that happens to
  // share its id. Outside the draft provider this is the bare comparison.
  const isThisCartItemSelected = useIsCartItemSelected(
    selectedItemCartId,
    cartItem.cartItemId
  );
  const { cartItemModGroupClass, modGroupErrorClass, selectedModGroupClass } =
    useStyles();
  const childModifierGroups = convertModGroupToMap(
    Object.values({
      ...modGroupCartItem.childModifierGroups,
    }).filter((modGroup) => Object.keys(modGroup.selectedItems).length)
  );

  const childModifierGroupIDs = Object.keys(childModifierGroups);

  const requiredButNotSelectedModifierGroups = convertToMap(
    Object.values(modGroupCartItem.modifierGroups).filter(
      (modGroup) =>
        modGroup.minimumSelections > 0 &&
        !childModifierGroupIDs.includes(modGroup.id)
    )
  );
  const requiredButNotSelectedModifierGroupIDs = Object.keys(
    requiredButNotSelectedModifierGroups
  );

  const isModifierGroupSelected = (modGroup: ParsedModifierGroup): boolean => {
    if (!isThisCartItemSelected) return false;

    const isNestedContext = cartItem.cartItemId !== modGroupCartItem.cartItemId;

    if (isNestedContext && selectedModGroupPath) {
      const parentModGroupId = modGroupCartItem.parentModifierGroupId;
      if (parentModGroupId) {
        const constructedPath = `${parentModGroupId}__${modGroupCartItem.id}__${modGroup.id}`;
        return (
          selectedModGroupPath === constructedPath ||
          selectedModGroupPath.endsWith(`__${modGroup.id}`)
        );
      }
    }

    return selectedModGroupPath === modGroup.id;
  };

  const sortedModGroups = useMemo(
    () => sortModGroups(modGroupCartItem),
    [modGroupCartItem]
  );
  const selectModGroupToFocus = (
    e: MouseEvent,
    modGroup: ParsedModifierGroup
  ) => {
    if (!selectable) return;
    // HITL-1613 (follow-up): the modifier rows inside a cart card are a third
    // way into the centre pane, and `stopPropagation` below means they never
    // reach the card's own guard. In Shadow a LIVE ORDER row drives nothing:
    // this handler dispatches `selectItemAndResetNavigation` plus the mod-group
    // navigation, which is the same pane the card click was blocked from
    // opening.
    //
    // Before the return rather than after, so the click bubbles to the card —
    // which is itself inert in Shadow, so nothing happens either way, and the
    // two guards cannot disagree about whether the event was consumed.
    //
    // HITL-1617 scopes it. What HITL-1613 was protecting is the ORDER, not the
    // pane: opening a row was refused because the pane's writes landed in
    // `cartSlice`. A draft row's writes land in `draftCart`, which reaches
    // neither the customer nor the POS, so the guard would be refusing the one
    // thing Shadow exists to allow. The Live Order column is unchanged.
    if (isAgentActionBlocked && renderedCartScope === MAIN_CART_SCOPE) return;

    e.stopPropagation();

    const isNestedContext = cartItem.cartItemId !== modGroupCartItem.cartItemId;

    // Reset navigation and select item
    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: cartItem,
        itemCartId: cartItem.cartItemId,
        // HITL-1617: say WHICH cart's item 2 this is. Without it a My Cart mod
        // group would open the AI's item of the same number in the centre pane —
        // and the pane would then write into the order.
        cartScope: renderedCartScope,
      })
    );

    // Determine pathToItem based on context and available data
    const pathToItem = getModifierGroupPath(modGroup, isNestedContext);

    if (isNestedContext) {
      const parentModGroupId = modGroupCartItem.parentModifierGroupId;
      if (parentModGroupId) {
        // Set navigation to nested level
        dispatch(
          dialogActions.navigateToNestedLevel({
            parentModGroupId,
            firstNestedModGroup: modGroup,
            firstNestedModGroupPath: pathToItem,
          })
        );
      }
    }

    // Update selected modifier group and index
    dispatch(
      dialogActions.updateSelectedModGroup({
        modGroup,
        pathToItem,
      })
    );

    const modGroupIndex = sortedModGroups.findIndex(
      (mg) => mg.id === modGroup.id
    );
    if (modGroupIndex !== -1) {
      dispatch(dialogActions.setSelectedModGroupIndex(modGroupIndex + 1));
    }
  };

  const getModifierGroupPath = (
    modGroup: ParsedModifierGroup,
    isNestedContext: boolean
  ): string => {
    const cartModGroup = modGroup as any;

    // Check for existing pathToItem in selected items (BM case)
    if (cartModGroup.selectedItems) {
      const selectedItemsWithPathToItem = Object.values(
        cartModGroup.selectedItems
      ).filter((item: any) => item.pathToItem);

      if (selectedItemsWithPathToItem.length > 0) {
        return (selectedItemsWithPathToItem[0] as any).pathToItem;
      }
    }

    // Fallback: construct path based on context
    if (isNestedContext) {
      const parentModGroupId = modGroupCartItem.parentModifierGroupId;
      return `${parentModGroupId}__${modGroupCartItem.id}__${modGroup.id}`;
    }

    return modGroup.id;
  };

  return (
    <div className={`cartitem-mod-group ${cartItemModGroupClass}`}>
      {sortModGroups(modGroupCartItem).map((modGroup) => {
        const isSelected = isModifierGroupSelected(modGroup);
        if (childModifierGroupIDs.includes(modGroup.id)) {
          // constraint_name == CartModifierGroup.name (see
          // getMenuModuleConstraints); descend the error paths that target it.
          const groupName = childModifierGroups[modGroup.id]?.name;
          const childErrorItemPaths = errorNodePaths
            .filter((path) => path[0] === groupName)
            .map((path) => path.slice(1));
          return (
            <CartItemChildModGroup
              key={modGroup.id}
              quantity={quantity}
              cartItem={cartItem}
              modGroup={childModifierGroups[modGroup.id]}
              isSelected={isSelected}
              selectModGroupToFocus={selectModGroupToFocus}
              errorItemPaths={childErrorItemPaths}
            />
          );
        }
        if (requiredButNotSelectedModifierGroupIDs.includes(modGroup.id))
          return (
            <div
              key={modGroup.id}
              onClick={(e) => selectModGroupToFocus(e, modGroup)}
              className={isSelected ? selectedModGroupClass : undefined}
            >
              <TruncationTooltip title={modGroup.name}>
                <Typography
                  className={modGroupErrorClass}
                  color={Colors.Thunderbird}
                  variant="subtitle2"
                  children={'! ' + modGroup.name}
                />
              </TruncationTooltip>
            </div>
          );
        return null;
      })}
    </div>
  );
}
